import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { MemberProfileCompletionProvider } from './contexts/MemberProfileCompletionContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { ModulesProvider } from './contexts/ModulesContext';
import { ToastProvider } from './components/Toast';
import { ConfirmDialogProvider } from './components/ConfirmDialog';
import DashboardLayout from './components/DashboardLayout';
import { isOrgAdminRole, isSaasAdminRole } from './utils/iamRoles';
import { isPortalAdminPath, PORTAL_LOGIN_PATH, resolveMemberLoginPath, resolveFirstLoginPasswordPath, isMobileAuthPath, MOBILE_UNLOCK_PATH } from './utils/portalAdminNav';
import LoginEntry from './pages/LoginEntry';
import MobileLoginEntry from './pages/MobileLoginEntry';
import MobilePinUnlock from './pages/MobilePinUnlock';
import MobileSuggestPin from './pages/MobileSuggestPin';
import LoginAdmin from './pages/LoginAdmin';
import ForgotPassword from './pages/ForgotPassword';
import Register from './pages/Register';
import PublicEntreaLanding from './pages/PublicEntreaLanding';
import Home from './pages/Home';
import { Members, MemberDetails, MemberEdit } from './pages/members';
import Contributions from './pages/Contributions';
import Financial from './pages/Financial';
import ReleveCompteOrganisation from './pages/ReleveCompteOrganisation';
import MyFundLayout from './layouts/MyFundLayout';
import { MemberTransactionDetailsRoute } from './pages/MemberTransactionHistory';
import MeetingMinutes from './pages/MeetingMinutes';
import MeetingMinuteDetails from './pages/MeetingMinuteDetails';
import Documents from './pages/Documents';
import Calendar from './pages/Calendar';
import EventDetails from './pages/EventDetails';
import ActivityDetails from './pages/ActivityDetails';
import PublicEventDetails from './pages/PublicEventDetails';
import EventRegistrations from './pages/EventRegistrations';
import UsersManagement from './pages/UsersManagement';
import UserDetails from './pages/UserDetails';
import UserEdit from './pages/UserEdit';
import {
  ProfileLayout,
  ProfileOverview,
  ProfileMemberShell,
  ProfileAccountShell,
} from './pages/profile';
import Settings from './pages/Settings';
import RolesPermissions from './pages/RolesPermissions';
import AdminDashboard from './pages/AdminDashboard';
import Notifications from './pages/Notifications';
import MemberSupport from './pages/MemberSupport';
import AssistanceManagement from './pages/AssistanceManagement';
import Commissions from './pages/Commissions';
import ValidateAccount from './pages/ValidateAccount';
import ModulesManagement from './pages/ModulesManagement';
import OrganizationRegister from './pages/OrganizationRegister';
import OrganizationSettings from './pages/OrganizationSettings';
import OrganizationsList from './pages/OrganizationsList';
import OrganizationDetails from './pages/OrganizationDetails';
import SaasDashboard from './pages/SaasDashboard';
import SaasAuditLogs from './pages/SaasAuditLogs';
import SaasSystemInfo from './pages/SaasSystemInfo';
import AdminLedgera from './pages/AdminLedgera';
import ModuleProtectedRoute from './components/ModuleProtectedRoute';
import FirstLoginChangePassword from './pages/FirstLoginChangePassword';
import FirstLoginChangePasswordMobile from './pages/FirstLoginChangePasswordMobile';
import FullPageLoading from './components/FullPageLoading';
import MemberProfileGate from './components/MemberProfileGate';
import "./App.css";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user, isMobilePinRequired } = useAuth();
  const location = useLocation();
  const hasProject = !!localStorage.getItem('project_id');
  const loginPath = resolveMemberLoginPath({
    pathname: location.pathname,
    preferMobile: isMobileAuthPath(location.pathname),
  });

  if (loading) {
    return <FullPageLoading />;
  }

  if (!isAuthenticated || !hasProject) {
    return <Navigate to={loginPath} replace state={{ from: location }} />;
  }

  if (user?.must_change_password) {
    return <Navigate to={resolveFirstLoginPasswordPath(location.pathname)} replace />;
  }
  if (isMobilePinRequired()) {
    return <Navigate to={MOBILE_UNLOCK_PATH} replace />;
  }

  return <MemberProfileGate>{children}</MemberProfileGate>;
};

/** `/` : landing publique, ou redirection vers le tableau de bord si déjà connecté avec un projet. */
const PublicHomeGate = () => {
  const { isAuthenticated, loading, user, isMobilePinRequired } = useAuth();
  const location = useLocation();
  const hasProject = !!localStorage.getItem('project_id');

  if (loading) {
    return <FullPageLoading />;
  }

  if (isAuthenticated && hasProject) {
    if (user?.must_change_password) {
      return <Navigate to={resolveFirstLoginPasswordPath(location.pathname)} replace />;
    }
    if (isMobilePinRequired()) {
      return <Navigate to={MOBILE_UNLOCK_PATH} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <PublicEntreaLanding />;
};

// Public Route Component (redirect to home if already logged in)
const PublicRoute = ({ children, redirectTo = '/dashboard' }) => {
  const { isAuthenticated, loading, user, isMobilePinRequired } = useAuth();
  const location = useLocation();
  const hasProject = !!localStorage.getItem('project_id');

  if (loading) {
    return <FullPageLoading />;
  }

  if (!isAuthenticated) {
    return children;
  }
  if (hasProject && user?.must_change_password) {
    return <Navigate to={resolveFirstLoginPasswordPath(location.pathname)} replace />;
  }
  if (hasProject && isMobilePinRequired()) {
    return <Navigate to={MOBILE_UNLOCK_PATH} replace />;
  }
  return <Navigate to={redirectTo} replace />;
};

// SaaS scope route: SaaS admin or organization admin only
const SaasRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  const role = String(user?.role || '');
  if (isSaasAdminRole(role) || isOrgAdminRole(role)) {
    return children;
  }
  return <Navigate to="/dashboard" replace />;
};

// SaaS admin only route
const SaasAdminOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  const role = String(user?.role || '');
  if (isSaasAdminRole(role)) {
    return children;
  }
  return <Navigate to="/saas/organizations" replace />;
};

// For org_admin, open directly their own organization detail
const SaasOrganizationsEntry = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  const role = String(user?.role || '');
  const orgId = user?.organization_id || localStorage.getItem('organization_id');
  if (isOrgAdminRole(role) && orgId) {
    return <Navigate to={`/saas/organizations/${orgId}`} replace />;
  }
  return <OrganizationsList />;
};

function App() {
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search || '');
      const runtimeBackendUrl = String(params.get('backend_url') || '').trim();
      if (runtimeBackendUrl) {
        localStorage.setItem('uruganda_backend_url', runtimeBackendUrl);
        return;
      }
      const fromPlatform = String(localStorage.getItem('platform_backend_public_url') || '').trim();
      if (fromPlatform) {
        localStorage.setItem('uruganda_backend_url', fromPlatform);
        return;
      }
      const envUrl = String(process.env.REACT_APP_BACKEND_URL || '').trim();
      const host = String(window.location.hostname || '').trim();
      const isEnvLocalhost = /localhost|127\.0\.0\.1/i.test(envUrl);
      // Sur IP LAN en HTTP : ne pas forcer :8002 — le proxy webpack `/api` suffit.
      if (isEnvLocalhost && host && host !== 'localhost' && host !== '127.0.0.1') {
        if (window.location.protocol === 'http:') {
          localStorage.removeItem('uruganda_backend_url');
          localStorage.removeItem('platform_backend_public_url');
          return;
        }
        const proto = window.location.protocol === 'https:' ? 'https' : 'http';
        localStorage.setItem('uruganda_backend_url', `${proto}://${host}:8002`);
      }
    } catch {
      // no-op
    }
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <MemberProfileCompletionProvider>
        <SettingsProvider>
          <ModulesProvider>
        <ToastProvider>
            <ConfirmDialogProvider>
          <Routes>
          <Route path="/" element={<PublicHomeGate />} />
          {/* Public Routes */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <LoginEntry />
              </PublicRoute>
            } 
          />
          <Route
            path="/mobile/login"
            element={
              <PublicRoute>
                <MobileLoginEntry />
              </PublicRoute>
            }
          />
          <Route path="/mobile/unlock" element={<MobilePinUnlock />} />
          <Route path="/mobile/setup-pin" element={<MobileSuggestPin />} />
          <Route
            path="/portal/login"
            element={
              <PublicRoute redirectTo="/portal">
                <LoginAdmin />
              </PublicRoute>
            }
          />
          <Route path="/admin/login" element={<Navigate to="/portal/login" replace />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route 
            path="/register" 
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            } 
          />
          <Route 
            path="/register-organization" 
            element={
              <PublicRoute>
                <OrganizationRegister />
              </PublicRoute>
            } 
          />
          <Route 
            path="/validate-account/:token" 
            element={<ValidateAccount />}
          />

          <Route path="/first-login-password" element={<FirstLoginChangePassword />} />
          <Route path="/mobile/first-login-password" element={<FirstLoginChangePasswordMobile />} />
          
          {/* Public Event Details Route - For non-authenticated users */}
          <Route path="/events/public/:id" element={<PublicEventDetails />} />

          {/* Protected Routes */}
          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<ModuleProtectedRoute route="/"><Home /></ModuleProtectedRoute>} />
            <Route path="/profile" element={<ProfileLayout />}>
              <Route index element={<ProfileOverview />} />
              <Route path="fiche" element={<Navigate to="/profile" replace />} />
              <Route path="fiche/modifier" element={<Navigate to="/profile/edit" replace />} />
              <Route path="membre" element={<Navigate to="/profile" replace />} />
              <Route path="membre/modifier" element={<Navigate to="/profile/edit" replace />} />
              <Route
                path="edit"
                element={
                  <ModuleProtectedRoute route="/members">
                    <ProfileMemberShell />
                  </ModuleProtectedRoute>
                }
              >
                <Route index element={<MemberEdit />} />
              </Route>
              <Route
                path="compte"
                element={
                  <ModuleProtectedRoute route="/users">
                    <ProfileAccountShell />
                  </ModuleProtectedRoute>
                }
              >
                <Route index element={<UserDetails />} />
                <Route path="modifier" element={<UserEdit />} />
              </Route>
            </Route>
            <Route path="/members" element={<ModuleProtectedRoute route="/members"><Members /></ModuleProtectedRoute>} />
            <Route path="/members/:id" element={<ModuleProtectedRoute route="/members"><MemberDetails /></ModuleProtectedRoute>} />
            <Route path="/members/:id/edit" element={<ModuleProtectedRoute route="/members"><MemberEdit /></ModuleProtectedRoute>} />
            <Route path="/contributions" element={<ModuleProtectedRoute route="/contributions"><Contributions /></ModuleProtectedRoute>} />
            <Route path="/transactionDetails/:mode" element={<MemberTransactionDetailsRoute />} />
            <Route
              path="/historique-cotisations"
              element={<Navigate to="/transactionDetails/cotisations" replace />}
            />
            <Route
              path="/historique-allocations"
              element={<Navigate to="/transactionDetails/allocations" replace />}
            />
            <Route
              path="/approval-requests"
              element={<Navigate to="/notifications?tab=approvals" replace />}
            />
            <Route path="/financial" element={<Navigate to="/my-fund/financial" replace />} />
            <Route path="/releve-compte" element={<Navigate to="/my-fund" replace />} />
            <Route path="/ma-caisse" element={<Navigate to="/my-fund" replace />} />
            <Route path="/mon-organisation" element={<Navigate to="/my-fund" replace />} />
            <Route
              path="/my-fund"
              element={
                <ModuleProtectedRoute route="/financial">
                  <MyFundLayout />
                </ModuleProtectedRoute>
              }
            >
              <Route index element={<ReleveCompteOrganisation />} />
              <Route path="financial" element={<Financial />} />
            </Route>
            <Route path="/meetings/:id" element={<ModuleProtectedRoute route="/meeting-minutes"><MeetingMinuteDetails /></ModuleProtectedRoute>} />
            <Route path="/meetings" element={<ModuleProtectedRoute route="/meeting-minutes"><MeetingMinutes /></ModuleProtectedRoute>} />
            <Route path="/documents" element={<ModuleProtectedRoute route="/documents"><Documents /></ModuleProtectedRoute>} />
            <Route path="/calendar" element={<ModuleProtectedRoute route="/calendar"><Calendar /></ModuleProtectedRoute>} />
            <Route path="/events/:id" element={<ModuleProtectedRoute route="/calendar"><EventDetails /></ModuleProtectedRoute>} />
            <Route path="/activities/:id" element={<ModuleProtectedRoute route="/calendar"><ActivityDetails /></ModuleProtectedRoute>} />
            <Route path="/events/:id/registrations" element={<ModuleProtectedRoute route="/calendar"><EventRegistrations /></ModuleProtectedRoute>} />
            <Route path="/users" element={<ModuleProtectedRoute route="/users"><UsersManagement /></ModuleProtectedRoute>} />
            <Route path="/users/:id" element={<ModuleProtectedRoute route="/users"><UserDetails /></ModuleProtectedRoute>} />
            <Route path="/users/:id/edit" element={<ModuleProtectedRoute route="/users"><UserEdit /></ModuleProtectedRoute>} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/organization" element={<OrganizationSettings />} />
            <Route path="/modules" element={<ModulesManagement />} />
            <Route path="/positions" element={<Navigate to="/commissions" replace />} />
            {/* SaaS / Admin Organization Routes */}
            <Route path="/saas" element={<SaasRoute><SaasDashboard /></SaasRoute>} />
            <Route path="/saas/organizations" element={<SaasRoute><SaasOrganizationsEntry /></SaasRoute>} />
            <Route path="/saas/organizations/:id" element={<SaasRoute><OrganizationDetails /></SaasRoute>} />
            <Route path="/saas/admin/audit" element={<SaasAdminOnlyRoute><SaasAuditLogs /></SaasAdminOnlyRoute>} />
            <Route path="/saas/admin/system" element={<SaasAdminOnlyRoute><SaasSystemInfo /></SaasAdminOnlyRoute>} />
            <Route path="/roles" element={<ModuleProtectedRoute route="/roles-permissions"><RolesPermissions /></ModuleProtectedRoute>} />
            <Route path="/admin" element={<Navigate to="/portal" replace />} />
            <Route path="/admin/ledgera" element={<Navigate to="/portal/ledgera" replace />} />
            <Route path="/portal" element={<ModuleProtectedRoute route="/admin"><AdminDashboard /></ModuleProtectedRoute>} />
            <Route path="/portal/ledgera" element={<ModuleProtectedRoute route="/admin"><AdminLedgera /></ModuleProtectedRoute>} />
            <Route path="/portal/contributions" element={<ModuleProtectedRoute route="/contributions"><Contributions forceStaffView /></ModuleProtectedRoute>} />
            <Route path="/portal/assistance" element={<ModuleProtectedRoute route="/support"><AssistanceManagement forceStaffView /></ModuleProtectedRoute>} />
            <Route path="/portal/users" element={<ModuleProtectedRoute route="/users"><UsersManagement /></ModuleProtectedRoute>} />
            <Route path="/portal/roles" element={<ModuleProtectedRoute route="/roles-permissions"><RolesPermissions /></ModuleProtectedRoute>} />
            <Route path="/portal/settings" element={<Settings />} />
            <Route path="/portal/members" element={<ModuleProtectedRoute route="/members"><Members /></ModuleProtectedRoute>} />
            <Route path="/portal/members/:id" element={<ModuleProtectedRoute route="/members"><MemberDetails /></ModuleProtectedRoute>} />
            <Route path="/portal/members/:id/edit" element={<ModuleProtectedRoute route="/members"><MemberEdit /></ModuleProtectedRoute>} />
            <Route path="/portal/users/:id" element={<ModuleProtectedRoute route="/users"><UserDetails /></ModuleProtectedRoute>} />
            <Route path="/portal/users/:id/edit" element={<ModuleProtectedRoute route="/users"><UserEdit /></ModuleProtectedRoute>} />
            <Route path="/portal/commissions" element={<ModuleProtectedRoute route="/commissions"><Commissions /></ModuleProtectedRoute>} />
            <Route path="/portal/modules" element={<ModulesManagement />} />
            <Route path="/portal/positions" element={<Navigate to="/portal/commissions" replace />} />
            <Route path="/notifications" element={<ModuleProtectedRoute route="/notifications"><Notifications /></ModuleProtectedRoute>} />
            <Route path="/support" element={<MemberSupport />} />
            <Route path="/assistance" element={<ModuleProtectedRoute route="/support"><AssistanceManagement /></ModuleProtectedRoute>} />
            <Route path="/commissions" element={<ModuleProtectedRoute route="/commissions"><Commissions /></ModuleProtectedRoute>} />
          </Route>

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
            </ConfirmDialogProvider>
        </ToastProvider>
          </ModulesProvider>
        </SettingsProvider>
        </MemberProfileCompletionProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
